/* ------------------------------------------------------------------
   custom-social-post.js
   <custom-social-post> custom element:
   - autoplay (muted, loop) only while visible in the viewport
   - pauses videos on hidden slides
   - syncs dots/arrows with scroll-snap carousel
   - respects prefers-reduced-motion
   No dependencies, no conflicts with theme JS.
------------------------------------------------------------------- */
(() => {
  if (customElements.get('custom-social-post')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class CustomSocialPost extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.csp-track');
      this.slides = [...this.querySelectorAll('.csp-slide')];
      this.dots = [...this.querySelectorAll('.csp-dot')];
      this.prevBtn = this.querySelector('.csp-arrow--prev');
      this.nextBtn = this.querySelector('.csp-arrow--next');
      this.index = 0;
      this.inViewport = false;

      this.initViewportObserver();

      if (this.slides.length > 1) {
        this.initCarousel();
      }
    }

    disconnectedCallback() {
      this.viewportObserver?.disconnect();
      this.slideObserver?.disconnect();
    }

    /* Play/pause depending on page scroll position */
    initViewportObserver() {
      this.viewportObserver = new IntersectionObserver(
        ([entry]) => {
          this.inViewport = entry.isIntersecting;
          this.syncPlayback();
        },
        { threshold: 0.35 }
      );
      this.viewportObserver.observe(this);
    }

    /* Track which slide is snapped, update UI + playback */
    initCarousel() {
      this.slideObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio >= 0.6) {
              this.index = this.slides.indexOf(entry.target);
              this.updateUI();
              this.syncPlayback();
            }
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((s) => this.slideObserver.observe(s));

      this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot) =>
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.index)))
      );

      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this.goTo(this.index - 1); }
      });

      this.updateUI();
    }

    goTo(i) {
      const target = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
      target?.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }

    updateUI() {
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
      if (this.prevBtn) this.prevBtn.hidden = this.index === 0;
      if (this.nextBtn) this.nextBtn.hidden = this.index === this.slides.length - 1;
    }

    /* Only the visible slide's video plays; everything else pauses */
    syncPlayback() {
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        const shouldPlay = this.inViewport && i === this.index && !reducedMotion.matches;
        if (shouldPlay) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }
  }

  customElements.define('custom-social-post', CustomSocialPost);

  /* 2. Nav esterna: barra avanzamento + frecce (scroll di una card esatta) */
  if (!customElements.get('csp-feed-nav')) {
    class CspFeedNav extends HTMLElement {
      connectedCallback() {
        // Cerca il wrapper sia dentro l'elemento che nell'elemento genitore/pagina
        this.wrapper = this.querySelector('.csp-wrapper, .csp-wrapper--block') || 
                       this.closest('.csp-block-wrapper, section')?.querySelector('.csp-wrapper, .csp-wrapper--block') ||
                       document.querySelector('.csp-wrapper, .csp-wrapper--block');

        this.nav = this.querySelector('.csp-nav');
        this.bar = this.querySelector('.csp-nav__progress');
        this.prev = this.querySelector('.csp-nav__btn--prev');
        this.next = this.querySelector('.csp-nav__btn--next');

        if (!this.wrapper) return;

        this.onScroll = () => this.update();
        this.wrapper.addEventListener('scroll', this.onScroll, { passive: true });

        this.ro = new ResizeObserver(() => this.update());
        this.ro.observe(this.wrapper);

        this.prev?.addEventListener('click', (e) => {
          e.preventDefault();
          this.step(-1);
        });

        this.next?.addEventListener('click', (e) => {
          e.preventDefault();
          this.step(1);
        });

        // Delay minimo per garantire il calcolo corretto dopo il rendering del DOM
        setTimeout(() => this.update(), 50);
      }

      disconnectedCallback() {
        this.wrapper?.removeEventListener('scroll', this.onScroll);
        this.ro?.disconnect();
      }

      /* Calcola la misura esatta: larghezza di custom-social-post o .csp-card + gap */
      get stepSize() {
        const card = this.wrapper.querySelector('custom-social-post, .csp-card');
        if (!card) return this.wrapper.clientWidth * 0.8;
        const gap = parseFloat(getComputedStyle(this.wrapper).gap || getComputedStyle(this.wrapper).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      step(dir) {
        this.wrapper.scrollBy({
          left: dir * this.stepSize,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }

      update() {
        const max = this.wrapper.scrollWidth - this.wrapper.clientWidth;
        const overflow = max > 1;

        if (this.nav) this.nav.hidden = !overflow;
        if (!overflow) return;

        const x = Math.max(0, this.wrapper.scrollLeft);

        if (this.bar) {
          /* Calcola la percentuale reale di avanzamento dello scroll */
          const pct = max > 0 ? (x / max) * 100 : 0;
          this.bar.style.width = Math.min(100, Math.max(0, pct)) + '%';
        }

        if (this.prev) this.prev.disabled = x <= 2;
        if (this.next) this.next.disabled = x >= max - 2;
      }
    }
    customElements.define('csp-feed-nav', CspFeedNav);
  }
})();